import { baseValue as baseValueTemplate } from "../../../contexts/BudgetContext/calc-methods";
import type {
  AddedTypes,
  BaseValue,
  Budget,
  BudgetItemType,
  CalcOption,
  MainAppliedTaxes,
} from "../../../types/budget/budget";
import { BudgetItemTypeEnum } from "../../../types/budget/budget";
import { initialMainAppliedTaxes } from "../../../types/budget/budget";
import type {
  AppliedValues,
  DefaultTax,
  ServiceData,
  TaxData,
} from "../../../types/budget/service-item";
import { initialServiceData } from "../../../types/budget/service-item";
import { buildItemTypeListReplicatingServiceCategoriesToAllCalcs } from "../../../utils/budget-import-helper";
import { isSupplierToBeDefined } from "../../../utils/supplier";
import {
  effectiveTax,
  FORNECEDOR_A_DEFINIR_ID,
  FORNECEDOR_A_DEFINIR_NAME,
  isSharedItem,
  ITEM_TAX_KEYS,
  paramKey,
  parseNumber,
  SHARED_TAX_KEYS,
  type DraftItem,
  type DraftParams,
  type DraftState,
  type PaymentForm,
  type TaxKey,
  type TaxValue,
} from "./draft";

const TAX_FIELD: Record<TaxKey, keyof TaxData> = {
  ts: "serviceTax",
  iss: "iss",
  imp: "tribute",
  tmm: "fee",
  mk: "markup",
  imk: "supplier_tribute",
  com: "commission",
};

const LABEL_TO_CATEGORY = new Map<string, string>(
  Object.entries(BudgetItemTypeEnum).map(([key, label]) => [String(label), key])
);

const MONGO_ID = /^[a-f\d]{24}$/i;

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function toForma(calcOption: string | undefined): PaymentForm {
  if (
    calcOption === "A" ||
    calcOption === "B" ||
    calcOption === "C" ||
    calcOption === "D"
  ) {
    return calcOption;
  }
  return "SF";
}

function normalizeCategory(type: string | undefined): string {
  if (!type) return "";
  if (Object.prototype.hasOwnProperty.call(BudgetItemTypeEnum, type))
    return type;
  return LABEL_TO_CATEGORY.get(type) || "";
}

function formatCount(value: unknown): string {
  const n =
    typeof value === "number" ? value : parseNumber(String(value ?? ""));
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
}

function formatMoney(value: unknown): string {
  const n =
    typeof value === "number" ? value : parseNumber(String(value ?? ""));
  if (!n) return "";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function readTax(tax: DefaultTax | undefined): TaxValue | undefined {
  if (!tax || !tax.value) return undefined;
  const mode = tax.typeTax === "FIX_VALUE" ? "R$" : "%";
  return {
    mode,
    v: tax.value.toLocaleString("pt-BR", { maximumFractionDigits: 4 }),
  };
}

function writeTax(
  current: DefaultTax | undefined,
  tax: TaxValue | undefined
): DefaultTax {
  const base: DefaultTax = current
    ? { ...current }
    : { typeTax: "PERCENTAGE", value: 0, currencyValue: 0, valueBRL: 0 };
  if (!tax || tax.v === "") {
    return {
      ...base,
      typeTax: "PERCENTAGE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    };
  }
  return {
    ...base,
    typeTax: tax.mode === "R$" ? "FIX_VALUE" : "PERCENTAGE",
    value: parseNumber(tax.v),
    currencyValue: 0,
    valueBRL: 0,
  };
}

function toDraftSupplier(service: {
  supplierId?: string;
  supplierName?: string;
}): { id: string; name: string } {
  if (
    !service.supplierId ||
    service.supplierId === FORNECEDOR_A_DEFINIR_ID ||
    isSupplierToBeDefined(service)
  ) {
    return { id: FORNECEDOR_A_DEFINIR_ID, name: FORNECEDOR_A_DEFINIR_NAME };
  }
  return { id: service.supplierId, name: service.supplierName || "" };
}

function taxesFromData(
  taxData: Partial<TaxData> | undefined,
  keys: readonly TaxKey[]
): Partial<Record<TaxKey, TaxValue>> {
  const tx: Partial<Record<TaxKey, TaxValue>> = {};
  keys.forEach((key) => {
    const field = TAX_FIELD[key];
    const tax = readTax(taxData?.[field] as DefaultTax | undefined);
    if (tax) tx[key] = tax;
  });
  return tx;
}

function hasSharedValue(
  taxes: Partial<Record<TaxKey, TaxValue>> | undefined
): boolean {
  if (!taxes) return false;
  return SHARED_TAX_KEYS.some((key) => taxes[key] && taxes[key]!.v !== "");
}

export function budgetToDraft(
  budget: Partial<Budget> | null | undefined
): DraftState {
  const services = budget?.budgetServices || [];
  const collapsed = new Set<string>([FORNECEDOR_A_DEFINIR_ID, ""]);

  const items: DraftItem[] = services.map((service, index) => {
    const supplier = toDraftSupplier(service);
    if (supplier.id === FORNECEDOR_A_DEFINIR_ID && service.supplierId) {
      collapsed.add(service.supplierId);
    }
    const values = service.budgetValues;
    const tx = {
      ...taxesFromData(values?.taxData, SHARED_TAX_KEYS),
      ...taxesFromData(values?.taxData, ITEM_TAX_KEYS),
    };
    const overrides = values?.appliedValues;
    if (overrides?.markupOverride && overrides.markupOverrideValue) {
      tx.mk = readOverride(overrides.markupOverrideValue) || tx.mk;
    }
    if (overrides?.commissionOverride && overrides.commissionOverrideValue) {
      tx.com = readOverride(overrides.commissionOverrideValue) || tx.com;
    }
    if (
      overrides?.supplierTributeOverride &&
      overrides.supplierTributeOverrideValue
    ) {
      tx.imk = readOverride(overrides.supplierTributeOverrideValue) || tx.imk;
    }

    return {
      id: service._id || `loaded-${index}`,
      cat: normalizeCategory(service.type),
      svc: values?.serviceType || "",
      desc: values?.observation || "",
      qtd: formatCount(values?.quantity ?? 1),
      dias: formatCount(values?.daily ?? 1),
      valor: formatMoney(values?.taxData?.tariff?.value),
      forma: toForma(service.calcOption),
      forn: supplier.id,
      fornName: supplier.name,
      tx,
      source: clone(service),
    };
  });

  const params: DraftParams = {};
  (budget?.baseValues || []).forEach((row) => {
    if (row.version && row.version !== 0) return;
    const forma = toForma(row.calcOption);
    if (forma === "SF" || !row.category) return;
    const forn = collapsed.has(row.supplierId)
      ? FORNECEDOR_A_DEFINIR_ID
      : row.supplierId;
    const key = `${forma}|${forn}|${normalizeCategory(row.category)}`;
    const shared = taxesFromData(row.taxData, SHARED_TAX_KEYS);
    if (hasSharedValue(shared))
      params[key] = { ...(params[key] || {}), ...shared };
  });

  const nextItems = items.map((item) => {
    if (!isSharedItem(item)) return item;
    const key = paramKey(item);
    if (!hasSharedValue(params[key])) {
      const own: Partial<Record<TaxKey, TaxValue>> = {};
      SHARED_TAX_KEYS.forEach((taxKey) => {
        if (item.tx[taxKey]) own[taxKey] = item.tx[taxKey];
      });
      if (hasSharedValue(own)) params[key] = own;
    }
    if (!hasSharedValue(params[key])) return item;
    const tx = { ...item.tx };
    SHARED_TAX_KEYS.forEach((taxKey) => {
      delete tx[taxKey];
    });
    return { ...item, tx };
  });

  return { items: nextItems, params };
}

function readOverride(
  value: { calcType?: string; value?: number } | undefined
): TaxValue | undefined {
  if (!value?.value) return undefined;
  return {
    mode: value.calcType === "FIX_VALUE" ? "R$" : "%",
    v: value.value.toLocaleString("pt-BR", { maximumFractionDigits: 4 }),
  };
}

function applyTax(taxData: TaxData, key: TaxKey, tax: TaxValue | undefined) {
  const field = TAX_FIELD[key];
  if (field === "typeNfMm") return;
  (taxData as unknown as Record<string, DefaultTax>)[field] = writeTax(
    taxData[field] as DefaultTax,
    tax
  );
}

function overrideValue(tax: TaxValue | undefined) {
  if (!tax || tax.v === "") return undefined;
  return {
    calcType: tax.mode === "R$" ? "FIX_VALUE" : "PERCENTAGE",
    value: parseNumber(tax.v),
  };
}

/**
 * Copia o `_id` devolvido pelo pré-orçamento para `item.source`, casando a
 * ordem enviada (`sortOrder`, senão o índice) com o id local da linha.
 * Não altera `item.id` nem os campos digitados — a linha usa `item.id` como
 * key, e trocá-lo no meio da edição remontaria o input.
 */
export function stampSavedServiceIds(
  draft: DraftState,
  sentLocalIds: readonly string[],
  savedServices: ReadonlyArray<{ _id?: string; sortOrder?: number }>
): DraftState {
  const mongoIdByLocalId = new Map<string, string>();
  sentLocalIds.forEach((localId, index) => {
    const match =
      savedServices.find((service) => service.sortOrder === index) ??
      savedServices[index];
    const mongoId = match?._id;
    if (!localId || !mongoId || !MONGO_ID.test(mongoId)) return;
    mongoIdByLocalId.set(localId, mongoId);
  });

  if (mongoIdByLocalId.size === 0) return draft;

  let changed = false;
  const items = draft.items.map((item) => {
    const mongoId = mongoIdByLocalId.get(item.id);
    if (!mongoId) return item;
    const currentId = (item.source as { _id?: string } | undefined)?._id;
    if (currentId === mongoId) return item;
    changed = true;
    const source = item.source
      ? { ...(item.source as object), _id: mongoId }
      : { _id: mongoId };
    return { ...item, source };
  });

  return changed ? { ...draft, items } : draft;
}

export function draftToBudget(budget: Budget, draft: DraftState): Budget {
  const next = clone(budget);
  const services = draft.items.map((item, index) =>
    itemToService(item, draft, index)
  );

  const enabled: Record<string, boolean> = {};
  draft.items.forEach((item) => {
    if (item.cat) enabled[item.cat] = true;
  });

  const supplierList = supplierRows(draft, next.supplierList || []);
  const addedItemTypeList = addedRows(draft);

  next.budgetServices = services;
  next.supplierList = supplierList;
  next.addedItemTypeList = addedItemTypeList;
  next.itemTypeList =
    buildItemTypeListReplicatingServiceCategoriesToAllCalcs(enabled);
  next.baseValues = baseRows(draft, next.baseValues || []);
  next.mainAppliedTaxes = appliedRows(draft, next.mainAppliedTaxes || []);
  return next;
}

function itemToService(
  item: DraftItem,
  draft: DraftState,
  index: number
): ServiceData & { sortOrder: number } {
  const base = item.source
    ? clone(item.source as ServiceData)
    : clone(initialServiceData);
  if (!base._id || !MONGO_ID.test(base._id)) delete base._id;

  const values = base.budgetValues || clone(initialServiceData.budgetValues);
  values.serviceType = item.svc;
  values.observation = item.desc;
  values.quantity = parseNumber(item.qtd);
  values.daily = parseNumber(item.dias) || 1;
  values.taxData =
    values.taxData || clone(initialServiceData.budgetValues.taxData);
  values.taxData.tariff = {
    ...(values.taxData.tariff || {
      typeTax: "DAILY",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    }),
    typeTax: "DAILY",
    value: parseNumber(item.valor),
    currencyValue: parseNumber(item.valor),
    valueBRL: parseNumber(item.valor),
  };

  const sharedKeys = isSharedItem(item)
    ? SHARED_TAX_KEYS
    : item.forma === "SF"
    ? []
    : SHARED_TAX_KEYS;
  sharedKeys.forEach((key) =>
    applyTax(values.taxData, key, effectiveTax(draft, item, key))
  );
  if (item.forma === "SF") {
    SHARED_TAX_KEYS.forEach((key) => applyTax(values.taxData, key, undefined));
  }
  ITEM_TAX_KEYS.forEach((key) => applyTax(values.taxData, key, item.tx[key]));

  const markup = item.tx.mk;
  const commission = item.tx.com;
  const supplierTribute = item.tx.imk;
  const appliedValues: AppliedValues = {
    ...(values.appliedValues || {}),
    markupOverride: !!markup?.v,
    markupOverrideValue: overrideValue(markup),
    commissionOverride: !!commission?.v,
    commissionOverrideValue: overrideValue(commission),
    supplierTributeOverride: !!supplierTribute?.v,
    supplierTributeOverrideValue: overrideValue(supplierTribute),
  };
  values.appliedValues = appliedValues;
  base.budgetValues = values;
  base.type = item.cat;
  base.supplierId = item.forn || FORNECEDOR_A_DEFINIR_ID;
  base.supplierName = isFornecedor(item)
    ? FORNECEDOR_A_DEFINIR_NAME
    : item.fornName;
  base.calcOption = (item.forma === "SF" ? "" : item.forma) as CalcOption;
  if (!base.versions?.length) base.versions = [0];
  return { ...base, sortOrder: index };
}

function isFornecedor(item: DraftItem): boolean {
  return !item.forn || item.forn === FORNECEDOR_A_DEFINIR_ID;
}

function supplierRows(
  draft: DraftState,
  previous: Budget["supplierList"]
): Budget["supplierList"] {
  const seen = new Set<string>();
  const rows: Budget["supplierList"] = [];
  draft.items.forEach((item) => {
    if (item.forma === "SF") return;
    const supplierId = item.forn || FORNECEDOR_A_DEFINIR_ID;
    const key = `${supplierId}__${item.forma}`;
    if (seen.has(key)) return;
    seen.add(key);
    const existing = previous.find(
      (row) => row.supplierId === supplierId && row.calcOption === item.forma
    );
    rows.push(
      existing || {
        supplierId,
        calcOption: item.forma,
      }
    );
  });
  return rows;
}

function addedRows(draft: DraftState): AddedTypes[] {
  const seen = new Set<string>();
  const rows: AddedTypes[] = [];
  draft.items.forEach((item) => {
    if (item.forma === "SF" || !item.cat) return;
    const supplierId = item.forn || FORNECEDOR_A_DEFINIR_ID;
    const key = `${item.cat}__${supplierId}__${item.forma}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({
      type: item.cat as BudgetItemType,
      supplierId,
      calcOption: item.forma,
    });
  });
  return rows;
}

function baseRows(draft: DraftState, previous: BaseValue[]): BaseValue[] {
  const kept = previous.filter((row) => row.version && row.version !== 0);
  const seen = new Set<string>();
  const rows: BaseValue[] = [];

  draft.items.forEach((item) => {
    if (!isSharedItem(item)) return;
    const key = paramKey(item);
    if (seen.has(key)) return;
    seen.add(key);
    const [forma, supplierId, category] = key.split("|");
    const existing = previous.find(
      (row) =>
        (!row.version || row.version === 0) &&
        row.calcOption === forma &&
        row.supplierId === supplierId &&
        row.category === category
    );
    const row = existing ? clone(existing) : clone(baseValueTemplate);
    row.version = 0;
    row.calcOption = forma as CalcOption;
    row.supplierId = supplierId;
    row.category = category as BudgetItemType;
    row.taxData = row.taxData || clone(baseValueTemplate.taxData);
    SHARED_TAX_KEYS.forEach((taxKey) => {
      applyTax(row.taxData, taxKey, draft.params[key]?.[taxKey]);
    });
    ITEM_TAX_KEYS.forEach((taxKey) => applyTax(row.taxData, taxKey, undefined));
    rows.push(row);
  });

  return [...kept, ...rows];
}

function appliedRows(
  draft: DraftState,
  previous: MainAppliedTaxes[]
): MainAppliedTaxes[] {
  const kept = previous.filter((row) => row.version && row.version !== 0);
  const seen = new Set<string>();
  const rows: MainAppliedTaxes[] = [];

  draft.items.forEach((item) => {
    if (!isSharedItem(item)) return;
    const key = paramKey(item);
    if (seen.has(key)) return;
    seen.add(key);
    const [forma, supplierId, category] = key.split("|");
    const existing = previous.find(
      (row) =>
        (!row.version || row.version === 0) &&
        row.calcOption === forma &&
        row.supplierId === supplierId &&
        row.category === category
    );
    const row = existing ? clone(existing) : clone(initialMainAppliedTaxes);
    row.version = 0;
    row.calcOption = forma as CalcOption;
    row.supplierId = supplierId;
    row.category = category as BudgetItemType;
    row.appliedTaxes = {
      ...(row.appliedTaxes || initialMainAppliedTaxes.appliedTaxes),
      serviceTax: true,
      iss: true,
      tribute: true,
      fee: true,
      markup: true,
      supplier_tribute: true,
      commission: true,
    };
    rows.push(row);
  });

  return [...kept, ...rows];
}
